import { useMemo, useCallback } from "react";
import { useTran } from "@/hooks";

import { useAuthStore } from "@/features/auth";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { useOrganizationStore } from "@/features/organization";
import { useCompanyStore } from "@/features/company";
import { useUserStore } from "./useUserStore";
import { filterModulesByAudience } from "../utils/roles-permissions";
import {
  buildRoleOptions,
  resolveModuleAudience,
} from "../utils/user-role-options";
import { toJS } from "mobx";

const ADMIN_ROLES = ["system_admin", "business_unit"];
export const useUserListDerived = () => {
  const { t } = useTran();
  const { userRole, user } = useAuthStore();
  const isAdminRole = ADMIN_ROLES.includes(userRole);
  const currentUserId = user?.userId || user?.id;
  const companyId = user?.companyId;

  const { roles, permissionsModules } = useRolesPermissionsStore();
  const { createForm } = useUserStore();
  const { tree: organizationTree, loading: organizationLoading } =
    useOrganizationStore();
  const { companyOptions, companiesLoading } = useCompanyStore();
  const roleOptions = useMemo(
    () =>
      buildRoleOptions({
        roles,
        companyId,
        currentRoleKey: userRole,
        t,
      }),
    [companyId, roles, t, userRole],
  );

  const createModuleAudience = useMemo(
    () =>
      resolveModuleAudience({
        roleValue: createForm.role,
        roles,
        isCompanyAdmin: isAdminRole,
      }),
    [createForm.role, isAdminRole, roles],
  );

  const createPermissionModules = useMemo(
    () => filterModulesByAudience(permissionsModules, createModuleAudience),
    [createModuleAudience, permissionsModules],
  );

  const hasAnyAction = (actions = {}) => Object.values(actions).some(Boolean);

  const filterPermissionTree = useCallback(function walk(permissions = []) {
    return permissions
      .map((permission) => {
        const filteredChildren = walk(permission.children || []);
        const isAllowed =
          hasAnyAction(permission.actions) || filteredChildren.length > 0;
        if (!isAllowed) return null;
        return {
          ...permission,
          children: filteredChildren,
        };
      })
      .filter(Boolean);
  }, []);

  const rolesWithPermissions = useMemo(() => {
    return roles.map((role) => {
      const plainRole = toJS(role); // convert role to plain object

      const permissions = plainRole.isPermitAll
        ? toJS(permissionsModules) // full system permissions for permit all
        : filterPermissionTree(plainRole.permissions || []); // filtered permissions

      return {
        ...plainRole,
        permissions, // now plain JS, no proxies
      };
    });
  }, [roles, permissionsModules, filterPermissionTree]);

  const rolePermissionsToAllow = useCallback((permissions = []) => {
    const allow = [];

    permissions.forEach((module) => {
      const { key, actions, children = [] } = module || {};

      if (actions) {
        Object.entries(actions).forEach(([action, enabled]) => {
          if (enabled) {
            allow.push(`${key}:${action}`);
          }
        });
      }

      children.forEach((child) => {
        const { key: childKey, actions: childActions } = child || {};
        if (!childActions) return;

        Object.entries(childActions).forEach(([action, enabled]) => {
          if (enabled) {
            allow.push(`${childKey}:${action}`);
          }
        });
      });
    });

    return allow;
  }, []);

  const buildAllowAllPermissions = useCallback((modules = []) => {
    const normalize = (items = []) =>
      toJS(items).map((module) => ({
        key: module.key,
        title: module.title,
        actions: {
          create: true,
          update: true,
          view: true,
          delete: true,
        },
        children: module.subModule?.length ? normalize(module.subModule) : [],
      }));

    return normalize(modules);
  }, []);

  return {
    rolePermissionsToAllow,
    buildAllowAllPermissions,
    rolesWithPermissions,
    isAdminRole,
    currentUserId,
    roleOptions,
    createPermissionModules,
    companyOptions,
    companyLoading: companiesLoading,
    organizationTree,
    organizationLoading,
  };
};
